<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="'Frist verlängern'"
		@close="handleClose"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
		>
			<el-row>
				<el-col>
					<div class="form__label">Aktuelle Frist bis</div>
					<span>{{ $formatDate(deadlineDate) }}</span>
				</el-col>
			</el-row>

			<el-row>
				<el-col>
					<div class="form__label">Neue Frist</div>
					<el-form-item label="Enddatum" prop="lastValidDate">
						<el-date-picker
							v-model="request.lastValidDate"
							type="date"
							placeholder="TT.MM.JJJJ"
							:format="'DD.MM.YYYY'"
							:disabled-date="disabledDate"
						/>
					</el-form-item>
				</el-col>
			</el-row>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="save" :disabled="waiting" :loading="waiting">Frist verlängern</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import { texts, translateToGerman } from '../../utils/texts';
import type { FormInstance } from 'element-plus';
import { putStatementDeadline } from '@/requests/statements';
import { checkIfDateInPast } from '@/utils/search/searchUtils';
import { PutStatementDeadlineRequest } from '@/types/types';
import { validateForm, resetForm, dialogBeforeClose, getEndOfDayWithTime } from '@/utils/functions';
import moment from 'moment';

interface ExtendStatementDeadlineData {
	request: {
		lastValidDate: string;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	showErr: boolean;
	errMsg: string;
}

export default defineComponent({
	name: 'DialogExtendStatementDeadline',
	data(): ExtendStatementDeadlineData {
		return {
			request: {
				lastValidDate: '',
			},
			rules: {
				lastValidDate: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
			},
			id: 'extendStatementDeadlineForm',
			waiting: false,
			showErr: false,
			errMsg: texts.statements.deadlineExtensionFailed,
		};
	},
	components: {
		TheForm,
		TheButton,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		deadlineDate: {
			type: String,
			required: true,
		},
		statementId: {
			type: String,
			required: true,
		},
	},
	computed: {
		texts() {
			return texts;
		},
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	emits: ['reload', 'update:open'],
	methods: {
		disabledDate(date) {
			const d = moment(date);
			return d.isBefore(this.deadlineDate, 'day') || d.isSame(this.deadlineDate, 'day');
		},
		async save() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;

			let lastValidDate = this.request.lastValidDate;
			if (this.request.lastValidDate) {
				lastValidDate = getEndOfDayWithTime(this.request.lastValidDate, true);
			}

			const requestStatementDeadline = {
				statementId: this.statementId,
				lastValidDate: lastValidDate,
			} as PutStatementDeadlineRequest;

			const result = await putStatementDeadline(requestStatementDeadline);
			if (result.success) {
				this.handleClose(true);
			} else {
				const msg = translateToGerman(result.error);
				if (msg) {
					this.errMsg = msg;
				}

				this.showErr = true;
			}

			this.waiting = false;
		},
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);
			this.showErr = false;

			if (reload) {
				this.$emit('reload');
			}
			this.$emit('update:open', false);
		},
		dialogBeforeClose,
	},
});
</script>

<style lang="scss">
#extendStatementDeadlineForm {
	.el-row:first-child {
		margin-bottom: 2rem;
	}

	.form__label {
		font-size: 1rem;
		margin-bottom: 0.5rem;
		font-weight: 500;
	}
}
</style>
